import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from '../../context';
import { Confirm, Failure, Loading } from '../../bits';
import { Chip, Panel, useToast } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import { LOGIN_ROLES } from '../../../wire';
import type { LoginInvited, LoginRole, StaffLogin } from '../../../wire';
import { relative } from '../connections/words';
import { Dialog } from './Dialog';
import './logins.css';

/**
 * **Who can sign in** — a business's staff logins, on the Setup tab.
 *
 * What used to be the CLI's two halves in two places (`staff invite
 * --firebase-only` on a laptop, `--db-only` in the ops job;
 * `docs/runbooks/staff-logins.md` §3) is one button here, because `admin-api`
 * can reach both Identity Platform and the database. Every rule is still the
 * runbook's, and they are the server's to keep (`routes/logins.ts`); this card
 * only says them in words:
 *
 *  * **Nobody here ever sees a password.** An invite makes the login with none
 *    and Google emails the person a link to choose their own. The card says so
 *    in its first sentence, and nothing it receives could show one.
 *  * **A login whose email did not go out is still a login.** The answer says
 *    `invite_email: "failed"`; the card says that in a sentence with the Resend
 *    button beside it, rather than calling the invite a failure.
 *  * **The record and Google are shown side by side.** A login Google has and
 *    our record lacks is flagged with **Record it**; the other disagreements
 *    are a sentence each with the fix they need.
 *  * **Remove access asks first**, in words that say what changes: signed out
 *    now, nothing deleted, invite again to undo.
 *
 * The card reads its own list (`GET …/logins`) rather than the tenant payload:
 * the list is Identity Platform's as much as ours, and it reloads itself after
 * each change rather than reloading the page.
 */
export function Logins({ tenantId, name }: { tenantId: string; name: string }): ReactNode {
  const t = useT();
  const locale = useLocale();
  const api = useApi();
  const toast = useToast();
  const read = useRead(() => api.logins(tenantId), [tenantId]);
  const act = useAction();

  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState<StaffLogin | null>(null);
  const [removing, setRemoving] = useState<StaffLogin | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  /** The invite whose email did not go out, until it is re-sent or dismissed. */
  const [unsent, setUnsent] = useState<LoginInvited | null>(null);

  const list = read.value?.logins ?? [];
  const current = list.filter((l) => l.state !== 'removed');
  const removed = list.filter((l) => l.state === 'removed');
  const hasOwner = current.some((l) => l.role === 'owner' && l.state === 'active' && l.has_login);
  const who = (l: StaffLogin): string => (l.name !== '' ? l.name : l.email);

  const run = async (work: () => Promise<void>): Promise<boolean> => {
    const ok = await act.run(work);
    if (ok) read.reload();
    return ok;
  };

  const resend = (l: { uid: string; email: string }): void => {
    void run(async () => {
      await api.resendLogin(tenantId, l.uid);
      toast(t('logins.resent', { email: l.email }));
      setUnsent((u) => (u?.login.uid === l.uid ? null : u));
    });
  };

  const record = (l: StaffLogin): void => {
    void run(async () => {
      await api.recordLogin(tenantId, l.uid);
      toast(t('logins.recorded', { who: who(l) }));
    });
  };

  return (
    <Panel
      title={t('logins.title')}
      className="logins"
      actions={
        current.length > 0 ? (
          <Chip tone="ok">{t('logins.count', { count: current.length })}</Chip>
        ) : undefined
      }
    >
      <p className="lede">{t('logins.lede', { name })}</p>

      {read.value === null && read.loading ? <Loading what={t('logins.title')} /> : null}
      {read.failure !== null ? <Failure failure={read.failure} /> : null}

      {unsent !== null ? (
        <div className="warn logins-unsent" role="status">
          <span>{t('logins.mailFailed', { email: unsent.login.email })}</span>{' '}
          <button
            type="button"
            className="button button--sm"
            disabled={act.busy}
            onClick={() => resend(unsent.login)}
          >
            {t('logins.mailFailedAction')}
          </button>
        </div>
      ) : null}

      {read.value !== null && current.length === 0 ? (
        <p className="muted">{t('logins.none', { name })}</p>
      ) : null}

      {current.length > 0 ? (
        <ul className="logins-list">
          {current.map((l) => (
            <LoginRow
              key={l.uid}
              login={l}
              t={t}
              locale={locale}
              busy={act.busy}
              onResend={() => resend(l)}
              onRecord={() => record(l)}
              onChangeRole={() => {
                act.clear();
                setChanging(l);
              }}
              onRemove={() => {
                act.clear();
                setRemoving(l);
              }}
            />
          ))}
        </ul>
      ) : null}

      {read.value !== null && current.length > 0 && !hasOwner ? (
        <p className="warn" role="note">
          {t('logins.noOwner', { name })}
        </p>
      ) : null}
      {read.value?.truncated === true ? <p className="warn">{t('logins.truncated')}</p> : null}

      {changing === null && removing === null && !inviting && act.failure !== null ? (
        <Failure failure={act.failure} />
      ) : null}

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary button--sm"
          onClick={() => {
            act.clear();
            setInviting(true);
          }}
        >
          {t('logins.invite')}
        </button>
        {removed.length > 0 ? (
          <button type="button" className="linkish" onClick={() => setShowRemoved((v) => !v)}>
            {showRemoved
              ? t('logins.hideRemoved')
              : t('logins.showRemoved', { count: removed.length })}
          </button>
        ) : null}
      </div>

      {showRemoved && removed.length > 0 ? (
        <ul className="logins-list logins-list--muted">
          {removed.map((l) => (
            <li key={l.uid} className="logins-row">
              <div className="logins-row__main">
                <strong>{who(l)}</strong>
                {l.name !== '' ? <span className="muted"> · {l.email}</span> : null}
                <span className="muted">
                  {' · '}
                  {roleWord(t, l.role)} · {t('logins.removedState')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {inviting ? (
        <InviteDialog
          tenantId={tenantId}
          name={name}
          onClose={() => setInviting(false)}
          onInvited={(answer) => {
            setInviting(false);
            if (answer.invite_email === 'failed') setUnsent(answer);
            else toast(t('logins.invited', { email: answer.login.email }));
            read.reload();
          }}
        />
      ) : null}

      {changing !== null ? (
        <RoleDialog
          login={changing}
          who={who(changing)}
          busy={act.busy}
          failure={act.failure}
          onClose={() => {
            act.clear();
            setChanging(null);
          }}
          onSave={(role) => {
            const l = changing;
            void run(async () => {
              await api.setLoginRole(tenantId, l.uid, role);
              toast(t('logins.roleChanged', { who: who(l), role: roleWord(t, role) }));
              setChanging(null);
            });
          }}
        />
      ) : null}

      {removing !== null ? (
        <Confirm
          title={t('logins.removeTitle', { who: who(removing) })}
          consequence={t('logins.removeConsequence', { who: who(removing), name })}
          confirmLabel={t('logins.remove')}
          busy={act.busy}
          failure={act.failure}
          onCancel={() => {
            act.clear();
            setRemoving(null);
          }}
          onConfirm={() => {
            const l = removing;
            void run(async () => {
              await api.revokeLogin(tenantId, l.uid);
              toast(t('logins.removed', { who: who(l) }));
              setRemoving(null);
            });
          }}
        />
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* One login                                                           */
/* ------------------------------------------------------------------ */

function roleWord(t: T, role: LoginRole | null): string {
  switch (role) {
    case 'owner':
      return t('logins.role.owner');
    case 'staff':
      return t('logins.role.staff');
    case 'readonly':
      return t('logins.role.readonly');
    default:
      return t('logins.role.none');
  }
}

function roleWhat(t: T, role: LoginRole): string {
  switch (role) {
    case 'owner':
      return t('logins.role.owner.what');
    case 'staff':
      return t('logins.role.staff.what');
    case 'readonly':
      return t('logins.role.readonly.what');
  }
}

function flagSentence(t: T, flag: string): string {
  switch (flag) {
    case 'unrecorded':
      return t('logins.flag.unrecorded');
    case 'no_login':
      return t('logins.flag.no_login');
    case 'record_differs':
      return t('logins.flag.record_differs');
    case 'unknown_role':
      return t('logins.flag.unknown_role');
    default:
      return t('logins.flag.other');
  }
}

/**
 * "Ana · ana@lovespa.vi · Owner · signed in 2 days ago" and its buttons.
 *
 * The buttons are the ones that make sense for the login's state: a login
 * Google does not have cannot be emailed or re-roled, only removed from the
 * record; a switched-off one cannot be emailed (a password would not let it
 * in); an unrecorded one gets **Record it** first.
 */
function LoginRow({
  login,
  t,
  locale,
  busy,
  onResend,
  onRecord,
  onChangeRole,
  onRemove,
}: {
  login: StaffLogin;
  t: T;
  locale: string;
  busy: boolean;
  onResend: () => void;
  onRecord: () => void;
  onChangeRole: () => void;
  onRemove: () => void;
}): ReactNode {
  const named = login.name !== '' ? login.name : login.email;
  const state =
    login.state === 'disabled'
      ? t('logins.off')
      : login.last_sign_in_at === null
        ? t('logins.neverSignedIn')
        : t('logins.signedIn', { when: relative(login.last_sign_in_at, locale) });

  return (
    <li className="logins-row">
      <div className="logins-row__main">
        <div>
          <strong>{named}</strong>
          {login.name !== '' ? <span className="muted"> · {login.email}</span> : null}
        </div>
        <div className="muted logins-row__facts">
          <Chip tone={login.role === 'owner' ? 'accent' : 'plain'}>{roleWord(t, login.role)}</Chip>{' '}
          {login.has_login ? <span title={login.last_sign_in_at ?? undefined}>{state}</span> : null}
        </div>
        {login.flags.length > 0 ? (
          <ul className="logins-flags">
            {login.flags.map((f) => (
              <li key={f} className="warn">
                {flagSentence(t, f)}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="logins-row__actions">
        {login.flags.includes('unrecorded') && login.role !== null ? (
          <button type="button" className="button button--sm" disabled={busy} onClick={onRecord}>
            {t('logins.record')}
          </button>
        ) : null}
        {login.has_login && login.state === 'active' ? (
          <button type="button" className="button button--sm" disabled={busy} onClick={onResend}>
            {t('logins.resend')}
          </button>
        ) : null}
        {login.has_login ? (
          <button
            type="button"
            className="button button--sm"
            disabled={busy}
            onClick={onChangeRole}
          >
            {t('logins.changeRole')}
          </button>
        ) : null}
        <button
          type="button"
          className="button button--sm button--danger"
          disabled={busy}
          onClick={onRemove}
        >
          {t('logins.remove')}
        </button>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* The role choice, shared by invite and change                        */
/* ------------------------------------------------------------------ */

function RoleChoice({
  value,
  onChange,
  name,
}: {
  value: LoginRole;
  onChange: (role: LoginRole) => void;
  name: string;
}): ReactNode {
  const t = useT();
  return (
    <fieldset className="logins-roles">
      <legend>{t('logins.dialog.role')}</legend>
      {LOGIN_ROLES.map((role) => (
        <label key={role} className="logins-role">
          <input
            type="radio"
            name={name}
            checked={value === role}
            onChange={() => onChange(role)}
          />
          <span>
            <strong>{roleWord(t, role)}</strong>
            <span className="logins-role__what">{roleWhat(t, role)}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* Invite                                                              */
/* ------------------------------------------------------------------ */

function InviteDialog({
  tenantId,
  name,
  onClose,
  onInvited,
}: {
  tenantId: string;
  name: string;
  onClose: () => void;
  onInvited: (answer: LoginInvited) => void;
}): ReactNode {
  const t = useT();
  const api = useApi();
  const send = useAction();
  const [email, setEmail] = useState('');
  const [person, setPerson] = useState('');
  const [role, setRole] = useState<LoginRole>('staff');

  const address = email.trim();
  const plausible = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

  const submit = async (): Promise<void> => {
    let answer: LoginInvited | null = null;
    const ok = await send.run(async () => {
      answer = await api.inviteLogin(tenantId, {
        email: address,
        role,
        ...(person.trim() === '' ? {} : { name: person.trim() }),
      });
    });
    if (ok && answer !== null) onInvited(answer);
  };

  return (
    <Dialog title={t('logins.dialog.title', { name })} busy={send.busy} onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field__label">{t('logins.dialog.email')}</span>
          <input
            type="email"
            autoComplete="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('logins.dialog.name')}</span>
          <input
            type="text"
            autoComplete="off"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
          />
          <span className="field__hint">{t('logins.dialog.nameHint')}</span>
        </label>
        <RoleChoice value={role} onChange={setRole} name="invite-role" />
        <p className="muted">{t('logins.dialog.how')}</p>
        {send.failure !== null ? <Failure failure={send.failure} /> : null}
        <div className="modal__actions">
          <button type="button" className="button" onClick={onClose} disabled={send.busy}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={send.busy || !plausible}
          >
            {send.busy ? t('logins.dialog.sending') : t('logins.dialog.send')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Change role                                                         */
/* ------------------------------------------------------------------ */

function RoleDialog({
  login,
  who,
  busy,
  failure,
  onClose,
  onSave,
}: {
  login: StaffLogin;
  who: string;
  busy: boolean;
  failure: ReturnType<typeof useAction>['failure'];
  onClose: () => void;
  onSave: (role: LoginRole) => void;
}): ReactNode {
  const t = useT();
  const [role, setRole] = useState<LoginRole>(login.role ?? 'staff');
  return (
    <Dialog
      title={t('logins.roleDialog.title', { who })}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={busy || role === login.role}
            onClick={() => onSave(role)}
          >
            {busy ? t('common.working') : t('logins.roleDialog.save')}
          </button>
        </>
      }
    >
      <RoleChoice value={role} onChange={setRole} name="change-role" />
      <p className="muted">{t('logins.roleDialog.when')}</p>
      {failure !== null ? <Failure failure={failure} /> : null}
    </Dialog>
  );
}
